import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import api from '../lib/api';
import { branchesAPI, locationsAPI } from '../lib/api';
import { Newspaper, Plus, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { PostCard } from './newsfeed/PostCard';
import { PostDetailDialog } from './newsfeed/PostDetailDialog';
import { PostFormDialog } from './newsfeed/PostFormDialog';
import { NewsSettingsDialog } from './newsfeed/NewsSettingsDialog';
import { CategoryDialog } from './newsfeed/CategoryDialog';
import { newsAPI } from '../lib/api';
import { usePendingTasks } from '../contexts/PendingTasksContext';

const initialPostForm = {
  title: '', content: '', category_id: '', is_pinned: false,
  require_confirmation: false, allow_comments: true, visibility_type: 'all',
  visibility_branch_ids: [], visibility_location_ids: [],
};
const initialCatForm = { name: '', color: '#3B82F6', icon: 'tag' };

export default function Newsfeed() {
  const { user, hasPermission } = useAuth();
  const { reload: reloadPendingTasks } = usePendingTasks();
  const canCreate = hasPermission('news', 'execute');
  const canManage = hasPermission('news', 'edit');

  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [branches, setBranches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [settings, setSettings] = useState({
    comments_enabled: true, confirmations_enabled: true, pinning_enabled: true,
  });
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('all');

  const [postDialogOpen, setPostDialogOpen] = useState(false);
  const [detailPost, setDetailPost] = useState(null);
  const [saving, setSaving] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [catForm, setCatForm] = useState(initialCatForm);

  const [postForm, setPostForm] = useState(initialPostForm);
  const [editingPost, setEditingPost] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [postsRes, catsRes, settingsRes, branchesRes, locsRes] = await Promise.all([
        api.get('/news'),
        api.get('/news/categories'),
        api.get('/news/settings'),
        branchesAPI.getAll(),
        locationsAPI.getAll(),
      ]);
      setPosts(postsRes.data);
      setCategories(catsRes.data);
      setSettings(settingsRes.data);
      setBranches(branchesRes.data);
      setLocations(locsRes.data);
    } catch {
      toast.error('Fehler beim Laden des Newsfeeds');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Beim Öffnen der Seite als "gesehen" markieren -> Sidebar-Zähler verschwindet
  useEffect(() => {
    newsAPI.markSeen()
      .then(() => reloadPendingTasks())
      .catch(() => {});
  }, [reloadPendingTasks]);

  // Tiefenlink aus Dashboard-Widget: ?post=ID → Detail-Dialog öffnen
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const postId = searchParams.get('post');
    if (postId) {
      api.get(`/news/${postId}`)
        .then((res) => setDetailPost(res.data))
        .catch(() => {})
        .finally(() => {
          searchParams.delete('post');
          setSearchParams(searchParams, { replace: true });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredPosts = filterCategory === 'all'
    ? posts
    : posts.filter((p) => p.category_id === filterCategory);
  const pinnedPosts = filteredPosts.filter((p) => p.is_pinned);
  const normalPosts = filteredPosts.filter((p) => !p.is_pinned);
  const getCategoryById = (id) => categories.find((c) => c.id === id);

  // ==================== POST ACTIONS ====================
  const openPostDialog = (post = null) => {
    if (post) {
      setEditingPost(post);
      setPostForm({
        title: post.title, content: post.content,
        category_id: post.category_id || '',
        is_pinned: post.is_pinned, require_confirmation: post.require_confirmation,
        allow_comments: post.allow_comments !== false,
        visibility_type: post.visibility_type,
        visibility_branch_ids: post.visibility_branch_ids || [],
        visibility_location_ids: post.visibility_location_ids || [],
      });
      setUploadedFiles(post.attachments || []);
    } else {
      setEditingPost(null);
      setPostForm(initialPostForm);
      setUploadedFiles([]);
    }
    setPostDialogOpen(true);
  };

  const handleSavePost = async () => {
    if (!postForm.title.trim() || !postForm.content.trim()) {
      toast.error('Titel und Inhalt sind Pflichtfelder');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...postForm, attachment_ids: uploadedFiles.map((f) => f.id) };
      if (editingPost) {
        await api.put(`/news/${editingPost.id}`, payload);
        toast.success('Beitrag aktualisiert');
      } else {
        await api.post('/news', payload);
        toast.success('Beitrag veröffentlicht');
      }
      setPostDialogOpen(false);
      setUploadedFiles([]);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Beitrag wirklich löschen?')) return;
    try {
      await api.delete(`/news/${postId}`);
      toast.success('Beitrag gelöscht');
      setDetailPost(null);
      loadData();
    } catch {
      toast.error('Fehler beim Löschen');
    }
  };

  const openDetailPost = (post) => {
    api.get(`/news/${post.id}`).then((res) => setDetailPost(res.data));
  };

  const handleConfirm = async (postId) => {
    try {
      await api.post(`/news/${postId}/confirm`);
      toast.success('Lesebestätigung gesendet');
      loadData();
      if (detailPost?.id === postId) {
        const res = await api.get(`/news/${postId}`);
        setDetailPost(res.data);
      }
    } catch {
      toast.error('Fehler');
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim() || !detailPost) return;
    try {
      await api.post(`/news/${detailPost.id}/comments`, { content: commentText });
      setCommentText('');
      const res = await api.get(`/news/${detailPost.id}`);
      setDetailPost(res.data);
      loadData();
    } catch {
      toast.error('Fehler');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!detailPost) return;
    try {
      await api.delete(`/news/${detailPost.id}/comments/${commentId}`);
      const res = await api.get(`/news/${detailPost.id}`);
      setDetailPost(res.data);
      loadData();
    } catch {
      toast.error('Fehler');
    }
  };

  // ==================== FILE UPLOAD ====================
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    const results = [];
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: Datei zu groß (max. 10 MB)`);
        continue;
      }
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post('/news/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        results.push(res.data);
      } catch {
        toast.error(`${file.name}: Upload fehlgeschlagen`);
      }
    }
    setUploadedFiles((prev) => [...prev, ...results]);
    setUploading(false);
    e.target.value = '';
  };

  const removeUploadedFile = (fileId) =>
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));

  // ==================== SETTINGS / CATEGORIES ====================
  const handleSaveSettings = async () => {
    try {
      await api.put('/news/settings', settings);
      toast.success('Einstellungen gespeichert');
      setSettingsDialogOpen(false);
    } catch {
      toast.error('Fehler');
    }
  };

  const openCategoryDialog = (cat = null) => {
    setEditingCategory(cat);
    setCatForm(cat
      ? { name: cat.name, color: cat.color, icon: cat.icon || 'tag' }
      : initialCatForm
    );
    setCategoryDialogOpen(true);
  };

  const handleSaveCategory = async () => {
    if (!catForm.name.trim()) { toast.error('Name ist Pflichtfeld'); return; }
    try {
      if (editingCategory) {
        await api.put(`/news/categories/${editingCategory.id}`, catForm);
      } else {
        await api.post('/news/categories', catForm);
      }
      setCategoryDialogOpen(false);
      loadData();
      toast.success('Kategorie gespeichert');
    } catch {
      toast.error('Fehler');
    }
  };

  const handleDeleteCategory = async (catId) => {
    if (!window.confirm('Kategorie wirklich löschen?')) return;
    try {
      await api.delete(`/news/categories/${catId}`);
      loadData();
      toast.success('Kategorie gelöscht');
    } catch {
      toast.error('Fehler');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-8 space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="newsfeed-page">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
              <Newspaper className="h-5 w-5 sm:h-6 sm:w-6 text-primary flex-shrink-0" />
              Neuigkeiten
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Interne Nachrichten und Ankündigungen</p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            {canManage && (
              <Button variant="outline" size="sm" onClick={() => setSettingsDialogOpen(true)}
                className="flex-1 sm:flex-none"
                data-testid="btn-news-settings">
                Einstellungen
              </Button>
            )}
            {canCreate && (
              <Button onClick={() => openPostDialog()} size="sm" className="flex-1 sm:flex-none" data-testid="btn-new-post">
                <Plus className="h-4 w-4 mr-2" /> Neuer Beitrag
              </Button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1 sm:flex-wrap sm:overflow-visible">
          <Filter className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <Button size="sm" variant={filterCategory === 'all' ? 'default' : 'outline'}
            className="flex-shrink-0"
            onClick={() => setFilterCategory('all')} data-testid="filter-all">Alle</Button>
          {categories.map((cat) => (
            <Button key={cat.id} size="sm"
              variant={filterCategory === cat.id ? 'default' : 'outline'}
              onClick={() => setFilterCategory(cat.id)}
              data-testid={`filter-${cat.id}`}
              className="flex-shrink-0"
              style={filterCategory === cat.id
                ? { backgroundColor: cat.color }
                : { borderColor: cat.color, color: cat.color }}
            >{cat.name}</Button>
          ))}
          {canManage && (
            <Button size="sm" variant="ghost" onClick={() => openCategoryDialog()}
              className="text-muted-foreground flex-shrink-0">
              <Plus className="h-3 w-3 mr-1" /> Kategorie
            </Button>
          )}
        </div>

        {pinnedPosts.length > 0 && (
          <div className="space-y-3">
            {pinnedPosts.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                isPinned
                currentUserId={user?.id}
                canManage={canManage}
                settings={settings}
                category={getCategoryById(post.category_id)}
                onOpenDetail={openDetailPost}
                onEdit={openPostDialog}
                onDelete={handleDeletePost}
              />
            ))}
          </div>
        )}

        <div className="space-y-3">
          {normalPosts.length > 0 ? (
            normalPosts.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                isPinned={false}
                currentUserId={user?.id}
                canManage={canManage}
                settings={settings}
                category={getCategoryById(post.category_id)}
                onOpenDetail={openDetailPost}
                onEdit={openPostDialog}
                onDelete={handleDeletePost}
              />
            ))
          ) : (
            pinnedPosts.length === 0 && (
              <Card className="py-12">
                <CardContent className="flex flex-col items-center text-center">
                  <Newspaper className="h-12 w-12 text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground">Noch keine Beiträge vorhanden</p>
                  {canCreate && (
                    <Button className="mt-4" onClick={() => openPostDialog()}>
                      <Plus className="h-4 w-4 mr-2" /> Ersten Beitrag erstellen
                    </Button>
                  )}
                </CardContent>
              </Card>
            )
          )}
        </div>

        <PostDetailDialog
          post={detailPost}
          onOpenChange={setDetailPost}
          getCategoryById={getCategoryById}
          settings={settings}
          currentUserId={user?.id}
          canCreate={canCreate}
          canManage={canManage}
          commentText={commentText}
          setCommentText={setCommentText}
          onConfirm={handleConfirm}
          onAddComment={handleAddComment}
          onDeleteComment={handleDeleteComment}
        />

        <PostFormDialog
          open={postDialogOpen}
          onOpenChange={setPostDialogOpen}
          postForm={postForm}
          setPostForm={setPostForm}
          editingPost={editingPost}
          categories={categories}
          branches={branches}
          locations={locations}
          settings={settings}
          uploadedFiles={uploadedFiles}
          uploading={uploading}
          onFileUpload={handleFileUpload}
          onRemoveFile={removeUploadedFile}
          saving={saving}
          onSave={handleSavePost}
        />

        <NewsSettingsDialog
          open={settingsDialogOpen}
          onOpenChange={setSettingsDialogOpen}
          settings={settings}
          setSettings={setSettings}
          onSave={handleSaveSettings}
          categories={categories}
          setCategories={setCategories}
          onOpenCategoryDialog={openCategoryDialog}
          onDeleteCategory={handleDeleteCategory}
        />

        <CategoryDialog
          open={categoryDialogOpen}
          onOpenChange={setCategoryDialogOpen}
          editingCategory={editingCategory}
          catForm={catForm}
          setCatForm={setCatForm}
          onSave={handleSaveCategory}
        />
      </div>
    </DashboardLayout>
  );
}
